import {
  FluidAnchorPosition,
  FluidDisplayDensity,
  FluidElementState,
  FluidKeyedObject,
  FluidMenuItem,
  FluidSidebarConfig,
  FluidTheme,
  FluidThemeInterface,
  FluidThemePalette,
  FluidTreeMenuConfig,
  generateUniqueId,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Prop,
  State,
  Watch,
  writeTask,
} from '@stencil/core';
import { Subscription } from 'rxjs';

import {
  HEADER_ENTRY,
  SIDEBAR_LEFT_ENTRY,
  SIDEBAR_MENU_ITEMS,
  SIDEBAR_RIGHT_ENTRY,
} from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { ElementRegistry } from '../../utils/elements/element-registry.utils';
import {
  FluidMediaState,
  ResponsiveUtils,
} from '../../utils/resize/responsive-utils';
import { Utils } from '../../utils/utils';
import {
  SidebarContent,
  SidebarToggle,
  SidebarToggleProps,
} from './components/fluid-sidebar.components';

/**
 * @displayName Sidebar
 * @contextData FLUID Sidebar provides a flexible way to display internal/external navigation, in-page navigation (i.e. "jump links"), contextual information, or ancillary controls on a single page, collection of pages, or globally across all pages within a website.
 * <br/><br/>
 * In general, Sidebar's primary use case is to supplement header navigation by creating an "inverted L" navigation structure. It is also commonly used to provide "jump links" that vertically scroll the page to a certain position when clicked.
 * <br/><br/>
 * <strong>NOTE: Sidebar is meant to be implemented via FLUID Page, and should NOT be used as a standalone component. See walkthrough guide for more information: <a href="pages/developers/walkthroughs/fluid-sidebar:-implementation-&-tips">FLUID Sidebar: Implementation & Tips</a></strong>
 *
 * @slot - Content displayed within the Sidebar body. NOTE: if built-in Tree Menu is used (i.e. "menuItems" or "treeMenuConfig" Props), slotted content will be displayed BELOW the Tree Menu.
 */
@Component({
  tag: 'fluid-sidebar',
  styleUrl: 'fluid-sidebar.scss',
  shadow: true,
})
export class FluidSidebar implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Name for Sidebar's entry in global Element Registry.
   */
  private _registryName: string;

  /**
   * RxJS subscription to global Element Registry. Allows Sidebar to be
   * aware of other "sticky" components' positions within the DOM.
   * Stored as a variable to allow unsubscribing when component is
   * disconnected/removed from DOM.
   */
  private _registrySubscription$: Subscription;

  /**
   * RxJS subscription to global "media state" (monitors viewport size).
   */
  private _mediaStateSubscription$: Subscription;

  /**
   * Internal config object for embedded Tree Menu. Derived from merging
   * "menuItems" & "treeMenuConfig" Prop values.
   */
  private _treeMenuConfig: FluidTreeMenuConfig;

  /**
   * Reference to embedded Tree Menu component (if configured via
   * "menuItems" or "treeMenuConfig" Prop).
   */
  _treeMenuRef: HTMLFluidTreeMenuElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidSidebarElement;

  // ================================================================ //
  // -- States

  /**
   * Boolean tracking whether the viewport is tablet/mobile size (< 992px)
   * or desktop size (≥ 992px).
   */
  @State() _mobileViewport = false;

  /**
   * Indicates that the Sidebar is actively being animated open or
   * closed when true.
   */
  @State() _transitioning = false;

  /**
   * Offset distance from top of viewport to the top of a fixed Sidebar.
   * Compensates for height of Header and/or other "sticky" elements
   * positioned above a fixed Sidebar.
   */
  @State() _offsetTop = 0;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * When provided, adjusts the current theme's color palette & appearance.
   * NOTE: a theme palette acts as a "style override" applied ON TOP of
   * the current theme & isn't necessary for "standard" theme aesthetics.
   */
  @Prop({ mutable: true }) themePalette: FluidThemePalette;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Opens/expands the Sidebar when true, closes/collapses the Sidebar
   * when false. NOTE: by default, the "visible" Prop will be true
   * (expanded) at desktop size & false (collapsed) at mobile/tablet size
   * on first load. Setting an explicit initial value on "visible" will
   * override this default responsive behavior, so it is recommended to
   * leave this Prop out of FLUID Page's Sidebar config if Sidebar should
   * be open initially.
   */
  @Prop({ mutable: true }) visible = true;

  @Watch('visible')
  onVisibleChange() {
    this._transitioning = true;
    this.sidebarToggled.emit(this.visible);
  }

  /**
   * When true, Sidebar will have a fixed (i.e. "sticky") position within
   * the DOM when scrolling. NOTE: it is recommended to ALWAYS set this
   * Prop to true, especially if the Sidebar is used for navigation.
   */
  @Prop({ mutable: true }) fixed = false;

  @Watch('fixed')
  onFixedChange() {
    this.initializeSticky();
  }

  /**
   * Whether the Sidebar can be expanded / collapsed by the user. When
   * false, Sidebar will ALWAYS be visible/open at desktop size & will NOT
   * display a toggle button. NOTE: at tablet / mobile size, Sidebar WILL
   * become collapsible to save screen real estate (regardless of this
   * Prop's value).
   */
  @Prop({ mutable: true }) collapsible = true;

  /**
   * Whether to render the Sidebar on the left or right side of the
   * viewport. NOTE: this Prop is set automatically via FLUID Page (i.e.
   * value inferred from use of "leftSidebarConfig" or "rightSidebarConfig"
   * Prop).
   */
  @Prop({ mutable: true }) anchor: Exclude<
    FluidAnchorPosition,
    FluidAnchorPosition.TOP | FluidAnchorPosition.BOTTOM
  > = FluidAnchorPosition.LEFT;

  /**
   * When provided, Sidebar will render a built-in FLUID Tree Menu above
   * its primary unnamed content slot. Use this Prop INSTEAD OF the
   * "menuItems" Prop if you need access to ALL Tree Menu Props.
   */
  @Prop({ mutable: true }) treeMenuConfig: FluidTreeMenuConfig;

  @Watch('treeMenuConfig')
  onTreeMenuConfigChange() {
    this.updateTreeMenuConfig();
  }

  /**
   * When provided, Sidebar will render a built-in FLUID Tree Menu above
   * its primary unnamed content slot. This Prop renders a BASIC Tree Menu
   * - use "treeMenuConfig" Prop INSTEAD OF this Prop to access ALL of Tree
   * Menu's Props.
   */
  @Prop({ mutable: true }) menuItems: FluidMenuItem[];

  @Watch('menuItems')
  onMenuItemsChange() {
    this.updateTreeMenuConfig();
  }

  /*
   * This will set the aria-label and title for the Sidebar toggle button
   */
  @Prop({ mutable: true }) toggleButtonAriaLabel = 'Toggle Sidebar';

  /**
   * Sets Sidebar's width, padding, & embedded Tree Menu size to be more
   * or less compact.
   */
  @Prop({ mutable: true }) displayDensity: FluidDisplayDensity =
    FluidDisplayDensity.DEFAULT;

  /**
   * Allows Sidebar's Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidSidebarConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when Sidebar is opened or closed. The event's payload
   * is a boolean value that indicates its "visible" status. At desktop
   * size, this event is only emitted when the "collapsible" Prop is true.
   */
  @Event() sidebarToggled: EventEmitter<boolean>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action selectiveMapConfigToLocal - set local Props to "config" values
   * @action setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action setZIndex - sets component z-index relative to global base value
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    useI18n.bind(this)();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action initializeTreeMenuConfig - merge "menuItems" & "treeMenuConfig" Prop values
   * @action watchMedia - initializes watcher for changes in viewport size
   */
  componentWillLoad(): void {
    this.initializeTreeMenuConfig();
    this.watchMedia();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action _registryName - allows "initializeSticky()" to run
   * @action initializeSticky - adds Sidebar to global Element Registry & sets "sticky" position
   */
  componentDidLoad() {
    this._registryName =
      this.anchor === FluidAnchorPosition.LEFT
        ? SIDEBAR_LEFT_ENTRY
        : SIDEBAR_RIGHT_ENTRY;
    writeTask(() => this.initializeSticky());
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action removeElement - removes Sidebar from global Element Registry
   */
  disconnectedCallback() {
    ElementRegistry.removeElement(this._registryName, this.host);

    this._registrySubscription$?.unsubscribe();
    delete this._registrySubscription$;

    this._mediaStateSubscription$?.unsubscribe();
    delete this._mediaStateSubscription$;
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Prevents internal "_treeMenuConfig" variable from being initialized
   * if Tree Menu should not be rendered.
   */
  initializeTreeMenuConfig(): void {
    if (
      isNullOrUndefined(this.treeMenuConfig) &&
      isNullOrUndefined(this.menuItems)
    )
      return;
    this.updateTreeMenuConfig();
  }

  /**
   * Creates/updates internal "_treeMenuConfig" variable by merging
   * "menuItems" & "treeMenuConfig" Prop values. Also outputs console
   * warning if both Props are used simultaneously.
   */
  updateTreeMenuConfig(): void {
    !!this.treeMenuConfig &&
      !!this.menuItems &&
      Utils.consoleWarn(this.host, SIDEBAR_MENU_ITEMS);

    this._treeMenuConfig = {
      menuItems: this.menuItems,
      ...(this.treeMenuConfig ?? ({} as FluidTreeMenuConfig)),
    };
  }

  /**
   * Subscribes or unsubscribes Sidebar to global Element Registry. If
   * Sidebar is "sticky", retrieves its "top offset" from Element
   * Registry & determines if Header is also "sticky" - if so, uses the
   * top offset to position itself correctly below the sticky Header.
   */
  initializeSticky(): void {
    if (!this._registryName) return;

    if (!this.fixed && !!this._registrySubscription$) {
      ElementRegistry.removeElement(this._registryName, this.host);

      this._registrySubscription$?.unsubscribe();
      this._registrySubscription$ = undefined;
      this._offsetTop = 0;
    } else if (this.fixed && !this._registrySubscription$) {
      ElementRegistry.addElement(this._registryName, this.host);

      this._registrySubscription$ = ElementRegistry.registry$.subscribe(
        (registry: FluidKeyedObject<FluidElementState>) => {
          if (!registry[this._registryName]) return;

          const { offsetTop } = registry[this._registryName];
          if (registry[HEADER_ENTRY]?.dataStore?.sticky) {
            this._offsetTop = Math.max(0, offsetTop);
          } else {
            this._offsetTop = 0;
          }
        }
      );
    }
  }

  /**
   * When viewport size changes, collapses/expands Sidebar for best UX.
   * Collapses Sidebar to save space if: Sidebar is OPEN & viewport is at
   * TABLET/MOBILE size (regardless of "collapsible" Prop value). Opens
   * Sidebar if: Sidebar is NOT collapsible, viewport is at DESKTOP size,
   * and Sidebar is currently closed/collapsed (avoids a collapsed
   * Sidebar that can't be expanded).
   */
  watchMedia(): void {
    this._mediaStateSubscription$ = ResponsiveUtils.mediaState$.subscribe(
      (mediaState: FluidMediaState) => {
        const { breakpointLg } = mediaState;
        this._mobileViewport = !breakpointLg.matches;

        if (this._mobileViewport && this.visible) {
          this.toggleSidebar(false);
        } else if (
          !this._mobileViewport &&
          !this.collapsible &&
          !this.visible
        ) {
          this.toggleSidebar(true);
        }
      }
    );
  }

  /**
   * Opens/closes Sidebar. Called when Sidebar "toggle button" is clicked.
   */
  toggleSidebar(visible = !this.visible): void {
    if (this.visible === visible) return;
    this.visible = visible;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    const applyZIndex =
      this._mobileViewport && (this.visible || this._transitioning);

    const sidebarToggleProps: SidebarToggleProps = {
      ariaLabel:
        this._i18nStore?.translate(
          this.fluidTranslate?.toggleButtonAriaLabel
        ) || this.toggleButtonAriaLabel,
      anchor: this.anchor,
      collapsible: this.collapsible,
      theme: this.theme,
      visible: this.visible,
      mobileViewport: this._mobileViewport,
      offsetTop: this._offsetTop,
      transitioning: this._transitioning,
      onClickHandler: () => this.toggleSidebar(),
    };

    return (
      <div
        style={{
          ...(applyZIndex && { 'z-index': `${this.zIndex}` }),
          ...(!!this._offsetTop && { 'margin-top': `-${this._offsetTop}px` }),
        }}
        class={{
          [`fluid-sidebar theme-${this.theme}`]: true,
          [`theme-palette-${this.themePalette}`]: !!this.themePalette,
          'sidebar-open-desktop': this.visible && !this._mobileViewport,
          'transition-state': this._transitioning,
          'display-density-compact':
            this.displayDensity === FluidDisplayDensity.COMPACT,
        }}>
        <SidebarToggle {...sidebarToggleProps} />
        <div
          style={
            !!this._offsetTop && {
              'padding-top': `${this._offsetTop}px`,
            }
          }
          class={{
            [`sidebar-body sidebar-${this.anchor}`]: true,
            'sidebar-fixed': this.fixed,
            'sidebar-open-mobile': this.visible && this._mobileViewport,
            'transition-state': this._transitioning,
          }}>
          <SidebarContent host={this} treeMenuConfig={this._treeMenuConfig} />
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for Sidebar container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  // ================================================================ //
  // -- Internationalisation
  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: {
    toggleButtonAriaLabel: string;
  };
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;
}
